import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getOverlayContentProps } from '#pplx/frontend/aether/internal/floating-ui/getOverlayContentProps.js';
import { useHideWhenDetached } from '#pplx/frontend/aether/internal/floating-ui/useHideWhenDetached.js';
import { useCollisionPaddingWithTopInset } from '#pplx/frontend/aether/internal/popover-collision/useCollisionPaddingWithTopInset.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
import { CheckboxItem as RadixDropdownMenuCheckboxItem, Content as RadixDropdownMenuContent, Group as RadixDropdownMenuGroup, Item as RadixDropdownMenuItem, ItemIndicator as RadixDropdownMenuItemIndicator, Label as RadixDropdownMenuLabel, Portal as RadixDropdownMenuPortal, RadioGroup as RadixDropdownMenuRadioGroup, RadioItem as RadixDropdownMenuRadioItem, Root as RadixDropdownMenuRoot, Separator as RadixDropdownMenuSeparator, Sub as RadixDropdownMenuSub, SubContent as RadixDropdownMenuSubContent, SubTrigger as RadixDropdownMenuSubTrigger, Trigger as RadixDropdownMenuTrigger, } from '@radix-ui/react-dropdown-menu';
import { forwardRef } from 'react';
/**
 * Behavior-only dropdown menu with accessible focus management, keyboard navigation,
 * typeahead, portal, and collision handling.
 */
function DropdownMenuPrimitiveRoot({ triggerElement, children, defaultOpen, open, onOpenChange, offsetPx, modal, portalContainer, hideWhenDetached, collisionPadding, collisionBoundary, ...contentProps }) {
    const providerHideWhenDetached = useHideWhenDetached();
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const resolvedCollisionPadding = useCollisionPaddingWithTopInset(collisionPadding, collisionBoundary);
    const overlayContentProps = getOverlayContentProps({
        ...contentProps,
        collisionBoundary,
        collisionPadding: resolvedCollisionPadding,
        sideOffset: offsetPx,
        hideWhenDetached: hideWhenDetached ?? providerHideWhenDetached,
    }, { containScrollEvents: shouldUseActivationOrder });
    return (_jsxs(RadixDropdownMenuRoot, { defaultOpen: defaultOpen, open: open, onOpenChange: onOpenChange, modal: modal, children: [_jsx(RadixDropdownMenuTrigger, { asChild: true, children: triggerElement }), _jsx(RadixDropdownMenuPortal, { container: portalContainer, children: _jsx(RadixDropdownMenuContent, { ...overlayContentProps, children: children }) })] }));
}
/** Portaled submenu content that contains React mouse events within the overlay. */
const DropdownMenuPrimitiveSubContent = forwardRef(function DropdownMenuPrimitiveSubContent(props, ref) {
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const overlayContentProps = getOverlayContentProps(props, { containScrollEvents: shouldUseActivationOrder });
    return _jsx(RadixDropdownMenuSubContent, { ...overlayContentProps, ref: ref });
});
/** Behavior-only menu item with an Aether-owned composition contract. */
function DropdownMenuPrimitiveItem({ onPointerLeave, ...props }) {
    return (_jsx(RadixDropdownMenuItem, { ...props, asChild: props.asChild ?? true, onPointerLeave: (event) => {
            onPointerLeave?.(event);
            if (event.defaultPrevented)
                return;
            const menu = event.currentTarget.closest('[role="menu"]');
            const focusedElement = document.activeElement;
            const anotherSurfaceOwnsFocus = event.pointerType === 'mouse' &&
                menu !== null &&
                focusedElement !== null &&
                focusedElement !== document.body &&
                !menu.contains(focusedElement);
            if (anotherSurfaceOwnsFocus) {
                // Radix focuses the menu from its item pointer-leave handler. During the exit animation,
                // that can steal focus back from a surface the item just opened. Base UI may not have
                // this behavior; after migrating, remove this guard if the browser focus test stays green.
                event.preventDefault();
            }
        } }));
}
/** Behavior-only checkbox item that delegates rendering to the consumer-owned element. */
function DropdownMenuPrimitiveCheckboxItem(props) {
    return (_jsx(RadixDropdownMenuCheckboxItem, { asChild: true, ...props, onCheckedChange: (nextChecked) => props.onCheckedChange(nextChecked === true) }));
}
/** Behavior-only radio item that delegates rendering to the consumer-owned element. */
function DropdownMenuPrimitiveRadioItem(props) {
    return _jsx(RadixDropdownMenuRadioItem, { asChild: true, ...props });
}
/** Checked-state indicator that delegates rendering to the consumer-owned element. */
function DropdownMenuPrimitiveItemIndicator(props) {
    return _jsx(RadixDropdownMenuItemIndicator, { asChild: true, ...props });
}
/** Behavior-only dropdown menu primitives for consumer-owned presentation. */
export const DropdownMenuPrimitive = Object.assign(DropdownMenuPrimitiveRoot, {
    Item: DropdownMenuPrimitiveItem,
    CheckboxItem: DropdownMenuPrimitiveCheckboxItem,
    RadioGroup: RadixDropdownMenuRadioGroup,
    RadioItem: DropdownMenuPrimitiveRadioItem,
    ItemIndicator: DropdownMenuPrimitiveItemIndicator,
    Group: RadixDropdownMenuGroup,
    Label: RadixDropdownMenuLabel,
    Separator: RadixDropdownMenuSeparator,
    Sub: RadixDropdownMenuSub,
    SubTrigger: RadixDropdownMenuSubTrigger,
    SubContent: DropdownMenuPrimitiveSubContent,
    Portal: RadixDropdownMenuPortal,
});
//# sourceMappingURL=index.js.map